import { useCallback } from "react"

import { useMutation, useQuery } from "@tanstack/react-query"
import { AxiosError } from "axios"

import { axiosClient } from "$/http"

import Urls from "@/api/urls"
import { GoalsSortType } from "@/shared/types/sort"

import { QueryKey } from "../consts"
import { BehavioralChange, BehavioralChangeApi } from "../model/behavioral-goal"

const getBehavioralChange = async ({ queryKey }): Promise<BehavioralChangeApi[]> => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_key, { id, query }] = queryKey
    const { data } = await axiosClient.get(Urls.behavioralChange(id, query))
    return data
}

export function useBehavioralChangeData(participantId: number, query: GoalsSortType) {
    return useQuery<BehavioralChangeApi[], AxiosError, BehavioralChange[]>({
        queryKey: [QueryKey.BehavioralChange, { query, id: participantId }],
        queryFn: getBehavioralChange,
        select: useCallback(data => data.map(item => new BehavioralChange(item)), [])
    })
}

export function useArchivedBehavioralChangeData(participantId: number) {
    return useQuery<BehavioralChangeApi[], AxiosError, number[]>({
        queryKey: [QueryKey.BehavioralChange, { query: "archived", id: participantId }],
        queryFn: getBehavioralChange,
        select: useCallback(data => data.map(item => item.id), [])
    })
}

export function useCreateBehavioralChange(participantId: number) {
    return useMutation({
        mutationFn: (
            behavioralChange: Pick<
                BehavioralChangeApi,
                "description" | "order" | "additional_notes" | "projected_impact" | "title"
            >
        ) => axiosClient.post<{ id: number }>(Urls.createBehavioralChange(participantId), behavioralChange)
    })
}

export function useEditBehavioralChange(participantId: number) {
    return useMutation({
        mutationFn: (
            behavioralChange: Pick<
                BehavioralChangeApi,
                "description" | "order" | "additional_notes" | "projected_impact" | "id" | "title"
            >
        ) => axiosClient.put(Urls.editBehavioralChange(participantId, behavioralChange.id), behavioralChange)
    })
}

export function useArchiveBehavioralChange(participantId: number) {
    return useMutation({
        mutationFn: (behavioralChangeId: number) =>
            axiosClient.post(Urls.archiveBehavioralChange(participantId, behavioralChangeId))
    })
}

export function useUnarchiveBehavioralChange(participantId: number) {
    return useMutation({
        mutationFn: (behavioralChangeId: number) =>
            axiosClient.post(Urls.unarchiveBehavioralChange(participantId, behavioralChangeId))
    })
}

export function useSortBehavioralChange(participantId: number) {
    return useMutation({
        mutationFn: (sort: { ids: number[] }) => axiosClient.post(Urls.sortBehavioralChange(participantId), sort)
    })
}
